<script setup>
import Header from "@/components/Header.vue";
import { ref } from "vue";
import Swal from "sweetalert2";
import { postContacto } from "@/utils/api_backend";
import heroImage from "@/assets/img/Evolve/img_7.jpg";
import { loadSlim } from "tsparticles-slim";

// CONFIG PARTICLES
const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#cc0000" },
    shape: { type: "circle" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};

const particlesInit = async (engine) => {
  await loadSlim(engine);
};

// FORM DATA
const nombre = ref("");
const correo = ref("");
const asunto = ref("");
const mensaje = ref("");
const cargando = ref(false);

// ENVIAR FORMULARIO
const enviarFormulario = async () => {
  cargando.value = true;

  try {
    await postContacto({
      nombre: nombre.value,
      correo: correo.value,
      asunto: asunto.value,
      mensaje: mensaje.value,
    });

    await Swal.fire({
      title: "¡Mensaje enviado!",
      text: "Gracias por contactarnos. Te responderemos pronto.",
      icon: "success",
      timer: 2500,
      showConfirmButton: false,
    });

    nombre.value = "";
    correo.value = "";
    asunto.value = "";
    mensaje.value = "";
  } catch (err) {
    console.error(err);

    await Swal.fire({
      title: "Error",
      text: "No se pudo enviar el mensaje.",
      icon: "error",
    });
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <section id="contacto" class="contact-wrapper">
    <!-- Fondo -->
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="contactParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>
    <!-- ENCABEZADO -->
    <div class="section-title">
      <Header />

      <p class="subtitle">
        ¿Tienes dudas? <br />
        Escríbenos y con gusto te ayudamos.
      </p>
    </div>

    <!-- GRID RESPONSIVO -->
    <div class="contact-grid" data-aos="fade-up">
      <!-- Columna izquierda -->
      <div class="left-grid">
        <!-- Dirección -->
        <article class="info-card">
          <div class="info-card__icon">
            <i class="bi bi-geo-alt"></i>
          </div>
          <h3 class="info-card__title">Dirección</h3>
          <p class="info-card__value">Valdez Sánchez #274, Saltillo, Coahuila</p>
        </article>

        <!-- Teléfono + Email -->
        <div class="subgrid">
          <article class="info-card">
            <div class="info-card__icon">
              <i class="bi bi-telephone"></i>
            </div>
            <h3 class="info-card__title">Teléfono</h3>
            <p class="info-card__value">844 696 5591</p>
          </article>

          <article class="info-card">
            <div class="info-card__icon">
              <i class="bi bi-envelope"></i>
            </div>
            <h3 class="info-card__title">Email</h3>
            <p class="info-card__value">evolvegrapplingpro@coudera.com</p>
          </article>
        </div>
      </div>

      <!-- Columna derecha -->
      <div class="form-wrapper">
        <form @submit.prevent="enviarFormulario">
          <input v-model="nombre" type="text" placeholder="Tu nombre" required />
          <input
            v-model="correo"
            type="email"
            placeholder="Tu correo electrónico"
            required
          />
          <input v-model="asunto" type="text" placeholder="Asunto" required />
          <textarea v-model="mensaje" placeholder="Mensaje" rows="3" required></textarea>

          <button type="submit" :disabled="cargando">
            {{ cargando ? "Enviando..." : "Enviar mensaje" }}
          </button>
        </form>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ------------------------------------------
   BACKGROUND
------------------------------------------- */
.contact-wrapper {
  position: relative;
  min-height: 100vh;
  padding: 2rem 0 4rem;
  overflow: hidden;
}

.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.75) contrast(1.2); /* 🔥 mejora radical del fondo */
  z-index: 1;
}

.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(20, 4, 2, 0.75) 50%,
    rgba(0, 0, 0, 0.95) 100%
  );
  z-index: 2;
}

#contactParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}

/* ------------------------------------------
   TITULO
------------------------------------------- */
.section-title {
  position: relative;
  z-index: 20;
  text-align: center;
  padding-top: calc(80px + 1.5rem);
  padding-bottom: 30px;
}

.section-title h2 {
  font-size: 2.4rem;
  color: white;
  font-weight: 800;
}

.subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px;
}

/* ------------------------------------------
   GRID PRINCIPAL
------------------------------------------- */
.contact-grid {
  position: relative;
  z-index: 10;
  width: min(1200px, 92vw);
  margin: auto;
  display: grid;
  gap: 1.8rem;

  grid-template-columns: 1fr 1fr;
}

/* ------------------------------------------
   TARJETAS — MISMO DISEÑO QUE INFORMACION.VUE
------------------------------------------- */
.info-card {
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(0, 170, 255, 0.22);
  border-radius: 18px;
  padding: 2.1rem 1.4rem;
  backdrop-filter: blur(10px);
  text-align: center;
  min-height: 150px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.02) inset, 0 10px 30px rgba(0, 0, 0, 0.25),
    0 0 22px rgba(220, 60, 60, 0.5);

  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.info-card:hover {
  transform: translateY(-6px);
  border-color: rgba(204, 0, 0, 0.55);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 14px 38px rgba(0, 0, 0, 0.35),
    0 0 30px rgba(204, 0, 0, 0.28);
}

.info-card__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(145deg, #cc0000, #ff0000);
  display: grid;
  place-items: center;
  margin-bottom: 1rem;
  box-shadow: 0 0 16px rgba(204, 0, 0, 0.6);
}

.info-card__icon i {
  color: white;
  font-size: 32px;
}

.info-card__title {
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.info-card__value {
  color: #d8eeff;
  font-size: 1rem;
  margin: 0;
}

/* ------------------------------------------
   SUBGRID (Teléfono + Email)
------------------------------------------- */
.left-grid {
  display: grid;
  gap: 1rem;
}
.subgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

/* ------------------------------------------
   FORMULARIO
------------------------------------------- */
.form-wrapper {
  background: rgba(255, 255, 255, 0.09);
  border-radius: 18px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(204, 0, 0, 0.22);
}
.form-wrapper:hover {
  border-color: rgba(204, 0, 0, 0.55);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 14px 38px rgba(204, 0, 0, 0.35),
    0 0 30px rgba(204, 0, 0, 0.28);
}

form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

input,
textarea {
  width: 100%;
  padding: 14px;
  border-radius: 10px;
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
}

button {
  margin-top: 1rem;
  align-self: center;
  padding: 12px 26px;
  border: none;
  border-radius: 50px;
  background: #cc0000;
  color: white;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease;
}
button:hover {
  background: #aa0000;
  box-shadow: 0 4px 14px rgba(204, 0, 0, 0.35);
  transform: translateY(-1px);
}

/* ------------------------------------------
   RESPONSIVE
------------------------------------------- */
@media (max-width: 1024px) {
  /* Tablet */
  .contact-grid {
    grid-template-columns: 1fr;
  }

  .subgrid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  /* Móvil */
  .subgrid {
    grid-template-columns: 1fr;
  }

  .contact-grid {
    gap: 1.4rem;
  }

  .subtitle {
    font-size: 1rem;
    padding: 0 20px;
    max-width: 100%;
  }
}

.smoke-layer {
  position: absolute;
  inset: 0;
  background: src("assets/backgrounds/image3.png");
  opacity: 0.22;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}

@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.2);
  }
  100% {
    transform: translateY(-20%) scale(1.25);
  }
}
</style>
